import React, { useState, useEffect } from 'react';
import { uniq } from 'lodash';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Formatter } from '../../../utils';
import getCustomTooltip from './CustomTooltip';
import { COLORS, DEFAULT_BAR_FIELDS, mapSumsToAxis, normalizeDataByDate } from './graphUtils';
import './styles.scoped.css';

const Tooltip = getCustomTooltip();

function getDisplayItems(sums: string[], yAxisMap: any, graphConfig: any) {
  const lineItems: any[] = [];
  const barItems: any[] = [];

  sums.forEach((sum, i) => {
    let itemType = graphConfig.displayType?.[sum];
    if (!itemType) itemType = DEFAULT_BAR_FIELDS.includes(sum) ? 'BAR' : 'LINE';

    if (itemType === 'BAR') {
      barItems.push(
        <Bar
          key={sum}
          type="monotone"
          yAxisId={yAxisMap[sum]}
          dataKey={sum}
          fill={COLORS.Bar[i % COLORS.Bar.length]}
        />,
      );
    }
    else {
      lineItems.push(
        <Line
          key={sum}
          dataKey={sum}
          yAxisId={yAxisMap[sum]}
          strokeWidth={2}
          stroke={COLORS.Line[i % COLORS.Line.length]}
        />,
      );
    }
  });

  // Note: Lines should be above Bars
  return [...barItems, ...lineItems];
}

export interface MixedGraphProps {
  data: any;
  sums: string[];
  groups: string[];
  dateRange: [Date, Date];
  graphConfig?: any;
}

function MixedGraph({ data, sums, groups, dateRange, graphConfig = {} }: MixedGraphProps) {
  const [graphData, setGraphData] = useState<any[]>([]);
  const yAxisMap = mapSumsToAxis(sums);
  const group = 'dateString';
  const uniqueYAxis = uniq(Object.values(yAxisMap));

  useEffect(() => {
    setGraphData(normalizeDataByDate(data, sums, groups[0], dateRange));
  }, [data, dateRange, groups, sums]);

  return (
    <ResponsiveContainer>
      <ComposedChart data={graphData} barSize={12}>
        <CartesianGrid strokeDasharray="2 2" />
        {Tooltip}
        <Legend
          iconType="circle"
          formatter={(val) => (
            <span className="legend-label">{Formatter.startCaseWithSpecialChars(val)}</span>
          )}
          iconSize={12}
        />
        <XAxis dataKey={group} tick={{ fontSize: 12 }} tickSize={8} scale="band" tickCount={6} />
        {uniqueYAxis.map((id) => (
          <YAxis key={`yaxis-${id}`} yAxisId={id} hide allowDecimals />
        ))}
        {getDisplayItems(sums, yAxisMap, graphConfig)}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default MixedGraph;
